import { useEffect, useRef, useState } from "react";
import { ActivityIndicator, Pressable, Text, View } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import * as Clipboard from "expo-clipboard";
import { useCSSVariable } from "uniwind";
import { posthog } from "@/shared/lib/posthog";
import type {
  CouponViewerState,
  CouponRevealState,
} from "@/features/events/lib/coupon";
import { getLockedCaption } from "@/features/events/lib/coupon";

export interface EventCouponCardProps {
  eventId: string;
  couponLabel: string;
  hasCouponCode: boolean;
  viewerState: CouponViewerState;
  reveal?: CouponRevealState;
  onPressLockedCoupon?: () => void;
}

const MASKED_CODE = "••••••••";

export function EventCouponCard({
  eventId,
  couponLabel,
  hasCouponCode,
  viewerState,
  reveal,
  onPressLockedCoupon,
}: EventCouponCardProps) {
  const primary = useCSSVariable("--color-primary") as string;
  const mutedForeground = useCSSVariable("--color-muted-foreground") as string;
  const [showCopied, setShowCopied] = useState(false);

  const isUnlocked = viewerState === "confirmed" && reveal?.status !== "error";
  const isCouponLoading = isUnlocked && reveal?.status === "loading";
  const couponCode =
    isUnlocked && reveal?.status === "ready" ? reveal.code : undefined;
  const isRevealedCode = isUnlocked && Boolean(couponCode);
  const isConfirmedNoCode =
    isUnlocked && reveal?.status === "ready" && !reveal.code;
  const isRevealed = isUnlocked && reveal?.status === "ready";

  const hasCapturedLockedView = useRef(false);
  useEffect(() => {
    if (!hasCapturedLockedView.current && viewerState !== "confirmed") {
      hasCapturedLockedView.current = true;
      posthog?.capture("coupon_locked_viewed", { event_id: eventId });
    }
  }, [viewerState, eventId]);

  const hasCapturedUnlock = useRef(false);
  useEffect(() => {
    if (!hasCapturedUnlock.current && isRevealed) {
      hasCapturedUnlock.current = true;
      posthog?.capture("coupon_unlocked", { event_id: eventId });
    }
    if (viewerState !== "confirmed") {
      hasCapturedUnlock.current = false;
    }
  }, [isRevealed, viewerState, eventId]);

  const handleCopy = async () => {
    if (!couponCode) return;
    await Clipboard.setStringAsync(couponCode);
    setShowCopied(true);
    posthog?.capture("coupon_code_copied", { event_id: eventId });
    setTimeout(() => setShowCopied(false), 2000);
  };

  const lockedCaption = getLockedCaption(viewerState, hasCouponCode);

  const isLoggedOut = viewerState === "logged-out";
  const Container = isLoggedOut ? Pressable : View;
  const containerProps = isLoggedOut
    ? {
        onPress: onPressLockedCoupon,
        accessibilityRole: "button" as const,
        accessibilityLabel: "Cupom bloqueado. Entre para ver o cupom",
      }
    : {};

  return (
    <View className="mb-10">
      <Text
        className="text-foreground font-display mb-3 text-xl font-semibold"
        accessibilityRole="header"
      >
        Cupom
      </Text>
      <Container
        className="bg-card-event rounded-lg p-5 active:opacity-70"
        {...containerProps}
      >
        <View className="mb-1 flex-row items-center gap-3">
          <Ionicons name="pricetag-outline" size={20} color={primary} />
          <Text className="text-foreground flex-1 text-base font-semibold">
            {couponLabel}
          </Text>
        </View>

        {isRevealedCode && (
          <View className="mt-2 flex-row items-center gap-3">
            <View className="bg-background flex-1 rounded-md px-3 py-2">
              <Text
                className="text-foreground text-base font-semibold tracking-widest"
                selectable
              >
                {couponCode}
              </Text>
            </View>
            <View>
              <Pressable
                onPress={handleCopy}
                hitSlop={8}
                accessibilityLabel="Copiar código do cupom"
                accessibilityRole="button"
              >
                <Ionicons name="copy-outline" size={20} color={primary} />
              </Pressable>
              {showCopied && (
                <View
                  className="bg-foreground absolute top-7 right-0 items-center rounded px-2 py-1"
                  style={{ width: 80 }}
                >
                  <Text
                    className="text-background text-xs font-medium"
                    numberOfLines={1}
                  >
                    Copiado
                  </Text>
                </View>
              )}
            </View>
          </View>
        )}

        {isConfirmedNoCode && (
          <View className="mt-2 flex-row items-center gap-2">
            <Ionicons name="checkmark-circle" size={18} color={primary} />
            <Text className="text-muted-foreground text-sm">
              Você está na lista
            </Text>
          </View>
        )}

        {isCouponLoading && (
          <View className="mt-2 flex-row items-center gap-3">
            <View className="bg-background flex-1 rounded-md px-3 py-2">
              <Text className="text-muted-foreground text-base font-semibold tracking-widest">
                {MASKED_CODE}
              </Text>
            </View>
            <ActivityIndicator
              size="small"
              color={mutedForeground}
              accessibilityLabel="Carregando cupom"
            />
          </View>
        )}

        {!isUnlocked && (
          <View className="mt-2 gap-2">
            {hasCouponCode && (
              <View className="mt-1 flex-row items-center gap-3">
                <View className="bg-background flex-1 rounded-md px-3 py-2">
                  <Text className="text-muted-foreground text-base font-semibold tracking-widest">
                    {MASKED_CODE}
                  </Text>
                </View>
                <Ionicons
                  name="lock-closed-outline"
                  size={18}
                  color={mutedForeground}
                />
              </View>
            )}
            <View className="flex-row items-center gap-2">
              {!hasCouponCode && (
                <Ionicons
                  name="lock-closed-outline"
                  size={16}
                  color={mutedForeground}
                />
              )}
              <Text className="text-muted-foreground text-sm">
                {lockedCaption}
              </Text>
            </View>
          </View>
        )}
      </Container>
    </View>
  );
}
